import React from 'react';

interface ChatHeaderProps {
  /**
   * Company or candidate name (role-aware)
   */
  displayName: string;

  /**
   * Company or candidate avatar URL
   */
  avatar?: string;

  /**
   * Online status
   */
  isOnline?: boolean;

  /**
   * Last seen timestamp
   */
  lastSeen?: string;

  /**
   * Is conversation starred/favorited
   */
  isStarred?: boolean;

  /**
   * Is info sidebar open
   */
  isInfoOpen?: boolean;

  /**
   * Is this a David (bot) conversation
   */
  isDavid?: boolean;

  /**
   * Callbacks
   */
  onStarToggle?: () => void;
  onMenuClick?: () => void;
  onProfileClick?: () => void;
}

/**
 * ChatHeader Component
 * Header for the chat view showing company/candidate info
 */
const ChatHeader: React.FC<ChatHeaderProps> = ({
  displayName,
  avatar,
  isOnline = false,
  lastSeen,
  isStarred = false,
  isInfoOpen = false,
  isDavid = false,
  onStarToggle,
  onMenuClick,
  onProfileClick,
}) => {
  /**
   * Format last seen timestamp
   */
  const formatLastSeen = (): string => {
    if (isOnline) {
      return 'Online';
    }

    if (!lastSeen) {
      return 'Offline';
    }

    try {
      const lastSeenTime = new Date(lastSeen);
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - lastSeenTime.getTime()) / (1000 * 60));

      if (diffInMinutes < 1) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `Last seen ${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
      } else if (diffInMinutes < 1440) {
        const hours = Math.floor(diffInMinutes / 60);
        return `Last seen ${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else {
        const days = Math.floor(diffInMinutes / 1440);
        return `Last seen ${days} day${days > 1 ? 's' : ''} ago`;
      }
    } catch (error) {
      return 'Offline';
    }
  };

  return (
    <div className="flex-shrink-0 flex flex-row justify-between items-center w-full py-4 px-6 bg-white/95 backdrop-blur-sm border-b border-[#E5E7EB] shadow-sm">
      {/* Left: Avatar + Name + Status (clickable to show info) */}
      <button
        onClick={onProfileClick}
        className="flex flex-row items-center gap-4 hover:bg-[#F8F9FA] -ml-2 pl-2 pr-8 py-2 rounded-xl transition-all duration-150 active:scale-[0.98] flex-1 min-w-0"
      >
        {/* Avatar */}
        <div className="relative">
          {isDavid ? (
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] flex items-center justify-center ring-2 ring-offset-2 shadow-sm transition-all duration-150 ${isInfoOpen ? 'ring-[#7C3AED]' : 'ring-[#F1F3F4]'}`}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="4" width="18" height="14" rx="3" stroke="white" strokeWidth="1.5"/>
                <circle cx="9" cy="11" r="1.5" fill="white"/>
                <circle cx="15" cy="11" r="1.5" fill="white"/>
                <path d="M8 20H16" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M12 18V20" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
          ) : avatar ? (
            <img
              src={avatar}
              alt={displayName}
              className={`w-12 h-12 rounded-xl object-cover ring-2 ring-offset-2 shadow-sm transition-all duration-150 ${isInfoOpen ? 'ring-[#015FA6]' : 'ring-[#F1F3F4]'}`}
            />
          ) : (
            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br from-[#015FA6] to-[#0176C9] flex items-center justify-center ring-2 ring-offset-2 shadow-sm transition-all duration-150 ${isInfoOpen ? 'ring-[#015FA6]' : 'ring-[#F1F3F4]'}`}>
              <span className="font-manrope font-semibold text-lg text-white">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Online status indicator — hidden for David */}
          {isOnline && !isDavid && (
            <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-[#22C55E] border-2 border-white rounded-full shadow-[0_0_0_2px_rgba(34,197,94,0.2)]" />
          )}
        </div>

        {/* Name + Last Seen */}
        <div className="flex flex-col gap-0.5 text-left">
          <h2 className="font-manrope font-semibold text-base text-[#1A1A1A] flex items-center gap-2">
            {displayName}
            {isDavid && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#7C3AED]/10 text-[#7C3AED] uppercase tracking-wider">AI</span>
            )}
          </h2>
          <p className={`font-manrope font-medium text-xs ${isDavid ? 'text-[#7C3AED]' : isOnline ? 'text-[#22C55E]' : 'text-[#9CA3AF]'}`}>
            {isDavid ? 'AI Assistant' : formatLastSeen()}
          </p>
        </div>

        {/* Info indicator */}
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={`ml-1 transition-transform duration-200 ${isInfoOpen ? 'rotate-180' : ''}`}
        >
          <path d="M6 9L12 15L18 9" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {/* Right: Action buttons */}
      <div className="flex flex-row items-center gap-1">
        {/* More menu button */}
        <button
          onClick={onMenuClick}
          className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[#F1F3F4] active:scale-95 transition-all duration-150"
          aria-label="More options"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="5" r="1.5" fill="#9CA3AF"/>
            <circle cx="12" cy="12" r="1.5" fill="#9CA3AF"/>
            <circle cx="12" cy="19" r="1.5" fill="#9CA3AF"/>
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ChatHeader;
